import React, { useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, Trash2, Mail } from "lucide-react";
import { toast } from "sonner";

const CATEGORIE_LABEL = { inkoop: "Inkoop", verkoop: "Verkoop", facturatie: "Facturatie" };
const HT_LABEL = { geen: "Geen handtekening", verzender: "Handtekening van de verzender", gebruiker: "Specifieke gebruiker" };

export function EigenEmailSjabloon({ tpl, afzenders, gebruikers, isAdmin, onSaved, onDeleted }) {
  const [f, setF] = useState({
    categorie: tpl.categorie, naam: tpl.naam || "", afzender_id: tpl.afzender_id || "",
    onderwerp: tpl.onderwerp || "", cc: tpl.cc || "", bcc: tpl.bcc || "", body: tpl.body || "",
    handtekening_type: tpl.handtekening_type || "geen",
    handtekening_gebruiker_id: tpl.handtekening_gebruiker_id || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const opslaan = async () => {
    if (!f.naam.trim()) { toast.error("Geef het sjabloon een naam"); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/email-templates/${tpl.id}`, f);
      toast.success("Sjabloon opgeslagen");
      onSaved?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    } finally { setSaving(false); }
  };

  const verwijderen = async () => {
    if (!window.confirm(`Sjabloon "${f.naam}" verwijderen?`)) return;
    try { await api.delete(`/email-templates/${tpl.id}`); toast.success("Verwijderd"); onDeleted?.(tpl.id); }
    catch (e) { toast.error(e?.response?.data?.detail || "Verwijderen mislukt"); }
  };

  const afzNaam = afzenders.find((a) => a.id === f.afzender_id);

  return (
    <AccordionItem value={tpl.id} className="border border-border rounded-lg bg-background overflow-hidden" data-testid={`eigen-sjabloon-${tpl.id}`}>
      <AccordionTrigger className="px-4 py-3 hover:no-underline">
        <div className="flex items-center gap-3 flex-1 min-w-0 text-left">
          <div className="p-1.5 rounded-md bg-secondary shrink-0"><Mail className="h-4 w-4 text-muted-foreground" /></div>
          <div className="min-w-0 flex-1">
            <div className="font-medium truncate">{f.naam || "Naamloos sjabloon"}</div>
            <div className="text-xs text-muted-foreground truncate">{f.onderwerp || "geen onderwerp"}</div>
          </div>
          <Badge variant="outline" className="shrink-0 text-[10px]">{HT_LABEL[f.handtekening_type]}</Badge>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4 space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label className="text-xs text-muted-foreground">Naam</Label>
            <Input value={f.naam} disabled={!isAdmin} onChange={(e) => set("naam", e.target.value)} data-testid={`sjabloon-naam-${tpl.id}`} /></div>
          <div><Label className="text-xs text-muted-foreground">Categorie</Label>
            <Select value={f.categorie} disabled={!isAdmin} onValueChange={(v) => set("categorie", v)}>
              <SelectTrigger data-testid={`sjabloon-categorie-${tpl.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(CATEGORIE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
        <div><Label className="text-xs text-muted-foreground">Afzender</Label>
          <Select value={f.afzender_id || "_"} disabled={!isAdmin} onValueChange={(v) => set("afzender_id", v === "_" ? "" : v)}>
            <SelectTrigger data-testid={`sjabloon-afzender-${tpl.id}`}><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="_">— Standaard afzender —</SelectItem>
              {afzenders.map((a) => <SelectItem key={a.id} value={a.id}>{a.naam || a.email}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-xs text-muted-foreground">Onderwerp</Label>
          <Input value={f.onderwerp} disabled={!isAdmin} onChange={(e) => set("onderwerp", e.target.value)} data-testid={`sjabloon-onderwerp-${tpl.id}`} /></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label className="text-xs text-muted-foreground">CC</Label>
            <Input value={f.cc} disabled={!isAdmin} onChange={(e) => set("cc", e.target.value)} placeholder="cc@voorbeeld.nl" data-testid={`sjabloon-cc-${tpl.id}`} /></div>
          <div><Label className="text-xs text-muted-foreground">BCC</Label>
            <Input value={f.bcc} disabled={!isAdmin} onChange={(e) => set("bcc", e.target.value)} placeholder="bcc@voorbeeld.nl" data-testid={`sjabloon-bcc-${tpl.id}`} /></div>
        </div>
        <div><Label className="text-xs text-muted-foreground">Emailtekst</Label>
          <Textarea rows={8} value={f.body} disabled={!isAdmin} onChange={(e) => set("body", e.target.value)} className="text-sm" data-testid={`sjabloon-body-${tpl.id}`} /></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label className="text-xs text-muted-foreground">Handtekening</Label>
            <Select value={f.handtekening_type} disabled={!isAdmin} onValueChange={(v) => set("handtekening_type", v)}>
              <SelectTrigger data-testid={`sjabloon-ht-${tpl.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(HT_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select></div>
          {f.handtekening_type === "gebruiker" && (
            <div><Label className="text-xs text-muted-foreground">Gebruiker</Label>
              <Select value={f.handtekening_gebruiker_id || "_"} disabled={!isAdmin} onValueChange={(v) => set("handtekening_gebruiker_id", v === "_" ? "" : v)}>
                <SelectTrigger data-testid={`sjabloon-ht-gebruiker-${tpl.id}`}><SelectValue placeholder="kies gebruiker" /></SelectTrigger>
                <SelectContent><SelectItem value="_">— kies gebruiker —</SelectItem>
                  {(gebruikers || []).map((g) => <SelectItem key={g.id} value={g.id}>{g.naam || g.email}</SelectItem>)}</SelectContent>
              </Select></div>
          )}
        </div>
        {isAdmin && (
          <div className="flex justify-between pt-2 border-t border-border">
            <Button variant="ghost" size="sm" onClick={verwijderen} className="text-destructive hover:text-destructive" data-testid={`sjabloon-verwijder-${tpl.id}`}><Trash2 className="h-4 w-4 mr-1" />Verwijderen</Button>
            <Button size="sm" onClick={opslaan} disabled={saving} data-testid={`sjabloon-opslaan-${tpl.id}`}><Save className="h-4 w-4 mr-1" />{saving ? "Opslaan…" : "Opslaan"}</Button>
          </div>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}
